import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedService } from '@shared/services/shared.service';
import { AddressComponent } from 'app/components/reusable/address/address.component';
import { BranchOperationComponent } from 'app/components/reusable/branch-operation/branch-operation.component';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { EntityOwnerComponent } from 'app/components/reusable/entity-owner/entity-owner.component';
import { ILicenseChange } from 'app/models/licenses/license-change';
import { InHomeCareChangeService } from 'app/services/license/in-home-care/in-home-care-change.service';
import { AdministratorApplicationComponent } from '../administrator-application/administrator-application.component';
import { IAddress } from 'app/models/address/address';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MY_DATE_FORMATS, MY_DATE_ADAPTER } from '../../license-date-format';
import { IInHomeCareChange } from 'app/models/licenses/in-home-care/in-home-care-change';

export interface ILicenseChangeDialog {
  licenseType: string | null;
  licenseId: number;
  licenseGuid: string | null;
  isSubunit?: boolean | null;
  entityId: number;
  entityType: string | null;
  originalValue: any;
  newValue: any;
  referenceAddresses?: IAddress[];
  changeAction: string | null;
}

@Component({
  selector: 'license-change',
  templateUrl: './in-home-care-change-dialog.component.html',
  styleUrls: ['./in-home-care-change-dialog.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    MY_DATE_FORMATS,
    MY_DATE_ADAPTER,
  ]
})

export class InHomeCareChangeDialogComponent implements OnInit {
  @ViewChild(DocumentComponent) documents!: DocumentComponent;
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @ViewChild(EntityOwnerComponent) owners!: EntityOwnerComponent;
  @ViewChild(BranchOperationComponent) branchOperations!: BranchOperationComponent;
  @ViewChild(AdministratorApplicationComponent) adminApplication!: AdministratorApplicationComponent;

  public newChangeAdded: boolean = false;
  public change: IInHomeCareChange = {
      id: 0,
      licenseId: 0,
      licenseGuid: '',
      entity: '',
      type: '',
      originalValue: '',
      newValue: '',
      newParent: '',
      subunits: [],
      dateRequested: '',
      requestedBy: '',
      effectiveDateOfChange: '',
      signature: '',
      title: '',
      dateSigned: '',
      requiresPayment: false,
      approved: false,
      denied: false,
      determinationBy: '',
      dateOfDetermination: ''
  }

  public thirtyDays: Date = new Date(Date.now());
  public validationErrors: string[] = [];
  public requiredDocumentTypes: string[] = [];
  public classificationDocumentTypes: string[] = ['Policy and Procedures'];
  public ownerDocumentTypes: string[] = ['Owner Agreement'];
  public newValue: string = '';
  public calculatedDistance: number = 0;
  public changeForm = new FormGroup({
    title: new FormControl(''),
    signature: new FormControl('', { validators: [Validators.required] }),
    dateSigned: new FormControl('', { validators: [Validators.required] }),
    effectiveDateOfChange: new FormControl('', { validators: [this.effectiveDateValidator()] }),
  });

  public sharedService = inject(SharedService);
  public changeService = inject(InHomeCareChangeService);
  public dialog = inject(MatDialog);

  constructor(@Inject(MAT_DIALOG_DATA) public changeData: ILicenseChangeDialog, private dialogRef: MatDialogRef<InHomeCareChangeDialogComponent>) {
    this.thirtyDays.setDate(this.thirtyDays.getDate() + 30);
  }

  ngOnInit(): void {
    this.createChange();
  }

  close(): void {
    this.dialogRef.close();
  }

  cancel(): void {
    if(this.newChangeAdded){
      this.changeData.originalValue = 'close';
      setTimeout(() => {
        this.deleteChange(this.change);
      }, 1000);
    }
    this.dialogRef.close();
  }

  getEmptyChange(): IInHomeCareChange {
    return {
      id: 0,
      licenseId: 0,
      licenseGuid: '',
      entity: '',
      type: '',
      originalValue: '',
      newValue: '',
      newParent: '',
      subunits: [],
      dateRequested: '',
      requestedBy: '',
      effectiveDateOfChange: '',
      signature: '',
      title: '',
      dateSigned: '',
      requiresPayment: false,
      approved: false,
      denied: false,
      determinationBy: '',
      dateOfDetermination: ''
    }
  }

  updateChange(): void {
    if(this.changeData.entityType !== this.sharedService.branchOperation && this.changeData.entityType !== this.sharedService.address){
      this.change.originalValue = this.changeData.originalValue;
    }

    if(this.changeData.entityType === this.sharedService.branchOperation && this.changeData.changeAction === this.sharedService.change){
      this.change.originalValue = this.changeData.originalValue;
      this.change.newValue = JSON.stringify(this.changeData.newValue);
    }
    else if(this.changeData.entityType === this.sharedService.address && this.changeData.changeAction === this.sharedService.change){
      this.change.originalValue = JSON.stringify(this.changeData.originalValue);
    }
    else if(this.changeData.entityType === this.sharedService.entityOwner && this.changeData.changeAction === this.sharedService.delete){
      this.change.originalValue = JSON.stringify(this.changeData.originalValue);
    }
    else if(this.changeData.entityType === this.sharedService.nurse || this.changeData.entityType === this.sharedService.classification){
      this.change.newValue = this.newValue;
    }
    else{
      this.change.newValue = this.changeData.newValue;
    }

    let form = this.changeForm.getRawValue();
    this.change.title = form.title;
    this.change.signature = form.signature;
    this.change.dateSigned = form.dateSigned;
    this.change.effectiveDateOfChange = form.effectiveDateOfChange;
  }

  updateChangeForm(change: IInHomeCareChange): void {
    this.changeForm.reset({
      title: change.title,
      signature: change.signature,
      dateSigned: change.dateSigned,
      effectiveDateOfChange: change.effectiveDateOfChange
    });
  }

  createChange(): void {
    this.change.licenseId = this.changeData.licenseId;
    this.change.entity = this.changeData.entityType;
    this.change.type = this.changeData.changeAction;

    if(this.change.entity === this.sharedService.entityOwner){
      this.requiredDocumentTypes = this.ownerDocumentTypes;
    }
    if(this.change.entity === this.sharedService.classification){
      this.requiredDocumentTypes = this.classificationDocumentTypes;
    }

    this.changeService.createChange(this.change).subscribe({
      next: change => {
        this.change = change;
      },
      error: e => {
        this.sharedService.toastr.error('Could Not Create Change');
        console.log('error creating change', e);
      },
      complete: () => {
        if(this.change.type === 'Duplicate'){
          this.sharedService.openNotice('A pending ' + this.change.entity + ' change request already exists.');
          this.close();
        }
        else{
          this.newChangeAdded = true;
        }
      }
    });
  }

  submitChange(): void {
    this.updateChange();

    if(this.changeData.entityType === this.sharedService.entityOwner){
      this.change.requiresPayment = true;
    }

    if(this.validateChange()){
      if(!this.changeData.isSubunit && this.changeData.entityType) {
        const paymentRequiredTransitions = {
          [this.sharedService.limited]: [
            this.sharedService.basic,
            this.sharedService.intermediate,
            this.sharedService.comprehensive
          ],
          [this.sharedService.basic]: [
            this.sharedService.intermediate,
            this.sharedService.comprehensive
          ],
          [this.sharedService.intermediate]: [
            this.sharedService.comprehensive
          ]
        };

        if (paymentRequiredTransitions[this.changeData.originalValue]?.includes(this.newValue)) {
          this.change.requiresPayment = true;
        }
      }

      this.changeService.submitChange(this.change).subscribe({
        error: e => {
          this.sharedService.toastr.error("Could not update change");
          console.log('error updating change', e);
        },
        complete: () => {
          this.newChangeAdded = false;
          this.sharedService.toastr.success("Change Request Submitted");
          this.close();
        }
      });
    }
    else{
      this.sharedService.openNotice('Your change request is missing information, or is an invalid request. Please verify all information before submitting.');
    }
  }

  validateChange(): boolean {
    this.validationErrors = [];
    if(this.changeData.entityType === this.sharedService.entityOwner){
      this.validateOwners();
    }
    if(this.changeData.entityType === this.sharedService.branchOperation){
      this.validateBranchOperations();
    }
    if(this.changeData.entityType === this.sharedService.administratorApplication){
      this.validateAdminApplication();
    }
    if(this.changeData.entityType === this.sharedService.nurse){
      if(this.newValue === ''){
        this.validationErrors.push('No name provided for new nurse');
      }
    }
    if(this.changeData.entityType === this.sharedService.classification){
      if(this.newValue === ''){
        this.validationErrors.push('Please select the new classification');
      }
    }
    if(this.changeData.entityType === this.sharedService.address){
      this.validateAddress();
    }
    return this.validationErrors.length === 0;
  }

  validateAddress(): void {
    if(this.addresses.addresses.length === 0){
      this.validationErrors.push('No address information provided');
    }
  }

  getAddressDistance(): void {
    let currentAddress: IAddress = this.changeData.newValue;
    if(this.changeData.entityType === this.sharedService.address){
      currentAddress = this.changeData.originalValue;
    }

    let newAddress: IAddress = this.addresses.getPhysicalAddress(this.addresses.addresses);
    if(newAddress === undefined){
      this.updateAddressDistance(0);
    }

    this.addresses.getDistanceBetweenAddressess(currentAddress, newAddress);
  }

  updateAddressDistance(distance: number): void {
    this.calculatedDistance = distance;
    this.change.approved = distance <= 60 && distance > 0;
  }

  validateBranchOperations(): void {
    if(this.changeData.changeAction === this.sharedService.add){
      if(this.branchOperations.operations.length === 0){
        this.validationErrors.push('No branch operation information provided');
      }
      this.branchOperations.operations.forEach(operation => {
        if(operation.distance > 60){
          this.validationErrors.push('Branch operation ' + operation.phone + ' is more than 60 miles from the agency.');
        }
      });
      this.validationErrors.push(...this.branchOperations.validateAddresses());
    }
    if(this.changeData.changeAction === this.sharedService.change){
      if(this.calculatedDistance > 60){
        this.validationErrors.push('The new branch operation is more than 60 miles from the agency.');
      }
    }
  }

  validateOwners(): void {
    if (this.changeData.changeAction !== this.sharedService.delete) {
      if (this.owners.owners.length === 0) {
        this.validationErrors.push('No owner information provided');
      }
      this.validationErrors.push(...this.owners.validateOwner());
    }
    let ownerAgreementFound = false;
    for (const document of this.documents.documents) {
      for (const type of document.types) {
        if (type.type === 'Owner Agreement') {
          ownerAgreementFound = true;
          break;
        }
      }
      if (ownerAgreementFound) break;
    }

    if (!ownerAgreementFound) {
      this.validationErrors.push('Missing owner agreement');
    }
  }

  validateAdminApplication(): void {
    let types: string[] = [];
    this.adminApplication.documents.documents.forEach((document) => {
      document.types.forEach((type) => {
        types.push(type.type);
      });
    });

    if(!this.adminApplication.addresses.getPhysicalAddress(this.adminApplication.addresses.addresses)){
      this.validationErrors.push('Missing administrator physical address');
    }

    if(!this.adminApplication.applicationForm.valid){
      this.validationErrors.push('Administrator applicant information is incomplete');
    }

    if(!types.includes('Resume')){
      this.validationErrors.push('Missing administrator resume');
    }

    if(!types.includes('High School Diploma or Equivalent')){
      this.validationErrors.push('Missing administrator proof of high school diploma or equivalent');
    }

    if(this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('Background Check Request')){
      this.validationErrors.push('Missing administrator background check request');
    }

    if(!this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('No Client Contact Plan')){
      this.validationErrors.push('Missing administrator no client contact plan');
    }

    if(this.adminApplication.applicationForm.get('hasManagementExperience')?.value){
      if(this.adminApplication.managementExperience.experiences.length === 0){
        this.validationErrors.push('Missing administrator management experience information');
      }
    }

    if(this.adminApplication.applicationForm.get('hasProfessionalExperience')?.value){
      if(this.adminApplication.professionalExperience.experiences.length === 0){
        this.validationErrors.push('Missing administrator professional experience information');
      }
      if(!types.includes('Proof of Credential')){
        this.validationErrors.push('Missing administrator proof of credentials for professional experience');
      }
    }
  }

  deleteChange(change: ILicenseChange): void {
    this.changeService.deleteChange(change.id).subscribe({
      complete: () => {
        this.newChangeAdded = false;
        this.change = this.getEmptyChange();
      },
      error: e => {
        this.sharedService.toastr.error("Error Deleting Change");
        console.log('error deleting change', e);
      }
    });
  }

  //Custom validators
  effectiveDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isAddress = this.changeData.entityType === "Address";
      const effectiveDate = control.value;
      if(!isAddress){
        return effectiveDate? null : { effectiveDateRequired: true };
      }
      return (this.calculatedDistance < 60 || effectiveDate) ? null : { effectiveDateRequired: true };
    }
  }

  validateEffectiveDate(): boolean {
    if (this.changeForm.hasError('effectiveDateRequired') && this.changeForm.get('effectiveDateOfChange')?.touched) {
      this.changeForm.get('effectiveDateOfChange')?.setErrors(['required']);
      return true;
    }
    return false;
  }
}
